import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertCircle, ChevronDown, Search, X } from 'lucide-react';
import { useLanguageStore } from '../../store/languageStore';

interface SelectOption {
  value: string | number;
  label: string;
}

interface SearchableSelectProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value'> {
  label?: string;
  error?: string;
  required?: boolean;
  options: SelectOption[];
  placeholder?: string;
  icon?: React.ReactNode;
  value?: string | number;
  onChange?: (value: string | number) => void;
  searchable?: boolean;
  disabled?: boolean;
  clearable?: boolean;
}

export const SearchableSelect: React.FC<SearchableSelectProps> = ({
  label,
  error,
  required = false,
  options,
  placeholder,
  icon,
  className = '',
  value,
  onChange,
  searchable = true,
  disabled = false,
  clearable = false,
  ...restProps
}) => {
  void restProps; // Absorb extra props from InputHTMLAttributes
  const { isRTL } = useLanguageStore();
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selectedOption = options.find((opt) => opt.value === value);

  const filteredOptions = searchable
    ? options.filter((option) =>
        option.label.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : options;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      // Focus search input when dropdown opens
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleSelect = (optionValue: string | number) => {
    onChange?.(optionValue);
    setIsOpen(false);
    setSearchQuery('');
  };

  const handleToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
      if (!isOpen) {
        setSearchQuery('');
      }
    }
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!disabled && clearable && value != null && value !== '') {
      onChange?.(undefined as unknown as string | number);
    }
  };

  return (
    <motion.div
      className="mb-4"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      {label && (
        <label
          className={`form-label fw-semibold mb-2 d-block ${isRTL ? 'text-end' : 'text-start'}`}
          style={{
            fontSize: '0.875rem',
            color: '#374151',
            fontFamily: 'inherit',
            letterSpacing: '0.3px',
          }}
        >
          {label}
          {required && <span className="text-danger ms-1">*</span>}
        </label>
      )}

      <div className="position-relative" ref={containerRef}>
        <motion.div
          className={`form-select ${error ? 'is-invalid' : ''} ${className} ${disabled ? 'opacity-50' : ''}`}
          style={{
            backgroundColor: disabled ? '#f3f4f6' : '#ffffff',
            border: error ? '1px solid #ef4444' : '1px solid rgba(0, 0, 0, 0.1)',
            borderRadius: '0.75rem',
            padding: icon ? (isRTL ? '0.75rem 3rem 0.75rem 1rem' : '0.75rem 1rem 0.75rem 3rem') : '0.75rem 1rem',
            fontSize: '1rem',
            color: selectedOption ? '#1f2937' : '#9ca3af',
            fontFamily: 'inherit',
            boxShadow: error
              ? '0 0 0 2px rgba(239, 68, 68, 0.2)'
              : isOpen
              ? '0 0 0 3px rgba(59, 130, 246, 0.1)'
              : '0 2px 8px rgba(0, 0, 0, 0.05)',
            cursor: disabled ? 'not-allowed' : 'pointer',
            borderColor: isOpen ? '#3b82f6' : error ? '#ef4444' : 'rgba(0, 0, 0, 0.1)',
            transition: 'all 0.2s ease-in-out',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            minHeight: '48px',
          }}
          onClick={handleToggle}
          whileHover={!disabled ? { scale: 1.01 } : {}}
          whileTap={!disabled ? { scale: 0.99 } : {}}
        >
          <div className="d-flex align-items-center flex-grow-1" style={{ overflow: 'hidden' }}>
            {icon && (
              <div
                className="flex-shrink-0 me-2"
                style={{
                  color: '#6b7280',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                {icon}
              </div>
            )}
            <span
              className="text-truncate"
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {selectedOption ? selectedOption.label : placeholder || 'Select...'}
            </span>
          </div>
          <div className="d-flex align-items-center gap-1 flex-shrink-0" style={{ marginLeft: isRTL ? '0' : '0.25rem', marginRight: isRTL ? '0.25rem' : '0' }}>
            {clearable && selectedOption && (
              <button
                type="button"
                onClick={handleClear}
                className="p-0 border-0 bg-transparent d-flex align-items-center justify-content-center"
                style={{ color: '#6b7280', cursor: 'pointer' }}
                aria-label="Clear"
              >
                <X size={18} />
              </button>
            )}
            <ChevronDown
              className="flex-shrink-0"
              size={18}
              style={{
                color: '#6b7280',
                transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s ease-in-out',
              }}
            />
          </div>
        </motion.div>

        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, y: -10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -10, scale: 0.95 }}
              transition={{ duration: 0.2 }}
              className="position-absolute w-100 mt-1"
              style={{
                backgroundColor: '#ffffff',
                border: '1px solid rgba(0, 0, 0, 0.1)',
                borderRadius: '0.75rem',
                boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
                zIndex: 1000,
                maxHeight: '300px',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                top: '100%',
                [isRTL ? 'right' : 'left']: 0,
              }}
            >
              {searchable && (
                <div
                  className="p-2 border-bottom"
                  style={{
                    borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
                  }}
                >
                  <div className="position-relative">
                    <Search
                      className="position-absolute"
                      size={18}
                      style={{
                        color: '#9ca3af',
                        [isRTL ? 'right' : 'left']: '12px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        pointerEvents: 'none',
                      }}
                    />
                    <input
                      ref={inputRef}
                      type="text"
                      className="form-control"
                      placeholder="Search..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      style={{
                        paddingLeft: isRTL ? '12px' : '40px',
                        paddingRight: isRTL ? '40px' : '12px',
                        border: '1px solid rgba(0, 0, 0, 0.1)',
                        borderRadius: '0.5rem',
                        fontSize: '0.875rem',
                      }}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                </div>
              )}

              <div
                style={{
                  maxHeight: '250px',
                  overflowY: 'auto',
                  overflowX: 'hidden',
                }}
              >
                {filteredOptions.length === 0 ? (
                  <div className="p-3 text-center text-muted">
                    <p className="mb-0 small">No options found</p>
                  </div>
                ) : (
                  filteredOptions.map((option) => {
                    const isSelected = option.value === value;
                    return (
                      <motion.div
                        key={option.value}
                        onClick={() => handleSelect(option.value)}
                        className="px-3 py-2"
                        style={{
                          cursor: 'pointer',
                          backgroundColor: isSelected ? '#eff6ff' : 'transparent',
                          borderLeft: isSelected ? '3px solid #3b82f6' : '3px solid transparent',
                          transition: 'all 0.15s ease-in-out',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                        }}
                        whileHover={{
                          backgroundColor: isSelected ? '#dbeafe' : '#f3f4f6',
                        }}
                      >
                        <span
                          style={{
                            color: isSelected ? '#3b82f6' : '#1f2937',
                            fontWeight: isSelected ? '600' : '400',
                            fontSize: '0.875rem',
                          }}
                        >
                          {option.label}
                        </span>
                        {isSelected && (
                          <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ type: 'spring', stiffness: 200, damping: 15 }}
                          >
                            <div
                              style={{
                                width: '20px',
                                height: '20px',
                                borderRadius: '50%',
                                backgroundColor: '#3b82f6',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#ffffff',
                                fontSize: '12px',
                                fontWeight: 'bold',
                              }}
                            >
                              ✓
                            </div>
                          </motion.div>
                        )}
                      </motion.div>
                    );
                  })
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <AnimatePresence mode="wait">
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -5, height: 0 }}
            animate={{ opacity: 1, y: 0, height: 'auto' }}
            exit={{ opacity: 0, y: -5, height: 0 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            className={`mt-2 ${isRTL ? 'text-end' : 'text-start'}`}
          >
            <div className="d-flex align-items-start gap-2">
              <motion.div
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ type: 'spring', stiffness: 200, damping: 15 }}
                className="flex-shrink-0"
                style={{ marginTop: '2px' }}
              >
                <AlertCircle className="h-4 w-4" style={{ color: '#ef4444' }} />
              </motion.div>
              <motion.p
                initial={{ opacity: 0, x: isRTL ? 5 : -5 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 }}
                className="mb-0 small fw-medium"
                style={{ color: '#ef4444', fontSize: '0.875rem' }}
              >
                {error}
              </motion.p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

